'use client';

import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  ButtonBase,
  DialogContent,
  Dialog as MuiDialog,
  Stack,
  Typography,
} from '@mui/material';
import { useFeatureInteracted } from '@repo/hooks/useFeatureInteracted';
import { FeatureViewed } from '@repo/segment/components/FeatureViewed';
import { type ReactNode, useCallback } from 'react';

interface MoreInfoDialogProps {
  id: string;
  open: boolean;
  title: string;
  content: ReactNode;
  onClose: () => void;
}

export const MoreInfoDialog = ({
  id = 'infographic',
  open,
  title,
  content,
  onClose,
}: MoreInfoDialogProps) => {
  const fireFeatureInteracted = useFeatureInteracted({
    entityName: `${id}_learn_more_pop_up_close`,
    entitySection: `${id}_learn_more_pop_up`,
  });

  const handleClose = useCallback(() => {
    fireFeatureInteracted();
    onClose();
  }, [fireFeatureInteracted, onClose]);

  return (
    <MuiDialog
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: '595px', md: '603px' },
            alignItems: 'flex-start',
            maxHeight: { xs: 'calc(85vh - 16px)', sm: 'auto' },
            marginTop: { xs: '15%', sm: '0%' },
          },
        },
      }}
      disableScrollLock
    >
      <DialogContent
        sx={{
          textAlign: 'left',
          p: 0,
          alignItems: 'flex-start',
          overflowY: 'auto',
          minWidth: { xs: '100%', sm: 'auto' },
        }}
      >
        <FeatureViewed
          entityName={`${id}_learn_more_pop_up`}
          entitySection={`${id}_learn_more_pop_up`}
        >
          <Stack sx={{ px: '2rem', py: '1.5rem' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: 2.5,
                width: '100%',
                mb: '1.5rem',
              }}
            >
              <Typography variant="textLargeBold" sx={{ width: '100%', fontWeight: '500' }}>
                {title}
              </Typography>
              <ButtonBase
                onClick={handleClose}
                aria-label="Close"
                sx={{ width: '24px', height: '24px', display: { xs: 'flex' }, marginTop: '0.5rem' }}
              >
                <CloseIcon sx={{ width: '24px', height: '24px' }} />
              </ButtonBase>
            </Box>
            {content}
          </Stack>
        </FeatureViewed>
      </DialogContent>
    </MuiDialog>
  );
};

export default MoreInfoDialog;
